import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFetch from "../../Hooks/useFetch";
import usePost from "../../Hooks/usePost";
import useInput from "../../form/Hooks/user-input";

const WebinarModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { fetchData } = useFetch();
  const { postData, isLoading: isSubmitting } = usePost();

  const [webinars, setWebinars] = useState([]);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: "" });

  // Form validation
  const validateName = (value) => value.trim().length >= 2;
  const validateEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const {
    value: fullName,
    isValid: fullNameIsValid,
    HasError: fullNameHasError,
    inputChangeHandler: fullNameChangeHandler,
    inputBlurHandler: fullNameBlurHandler,
    reset: resetFullName,
  } = useInput(validateName);

  const {
    value: email,
    isValid: emailIsValid,
    HasError: emailHasError,
    inputChangeHandler: emailChangeHandler,
    inputBlurHandler: emailBlurHandler,
    reset: resetEmail,
  } = useInput(validateEmail);

  const {
    value: phone,
    inputChangeHandler: phoneChangeHandler,
    inputBlurHandler: phoneBlurHandler,
    reset: resetPhone,
  } = useInput(() => true);

  useEffect(() => {
    if (isOpen) {
      loadWebinars();
    }
  }, [isOpen]);

  const loadWebinars = async () => {
    setLoading(true);
    try {
      const result = await fetchData("webinars");
      if (result?.data) {
        setWebinars(result.data);
        if (result.data.length === 1) {
          setSelectedWebinar(result.data[0]);
        }
      }
    } catch (error) {
      console.error("Error loading webinars:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    resetFullName();
    resetEmail();
    resetPhone();
    setSubmitStatus({ type: null, message: "" });
  };

  const handleClose = () => {
    resetForm();
    setSelectedWebinar(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ type: null, message: "" });

    if (!fullNameIsValid || !emailIsValid) {
      return;
    }

    try {
      const result = await postData(`webinars/${selectedWebinar.slug}/register`, {
        fullName,
        email,
        phone,
      });

      if (result?.success) {
        setSubmitStatus({
          type: "success",
          message: t("webinar.registrationSuccess"),
        });
        resetForm();
        // Close modal after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setSubmitStatus({
          type: "error",
          message: result?.message || t("webinar.registrationError"),
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: t("webinar.registrationError"),
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      isRTL ? "ar-EG" : "en-US",
      {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto ${
          isRTL ? "rtl" : "ltr"
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="bg-primary text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {selectedWebinar ? t("webinar.register") : t("webinar.title")}
          </h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : webinars.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t("webinar.noWebinars")}
            </div>
          ) : !selectedWebinar ? (
            // Webinar List
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">{t("webinar.selectWebinar")}</p>
              {webinars.map((webinar) => (
                <div
                  key={webinar._id}
                  onClick={() => setSelectedWebinar(webinar)}
                  className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <h3 className="font-semibold text-lg">{webinar.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {webinar.description}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {formatDate(webinar.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {webinar.time}
                    </span>
                  </div>
                  {webinar.capacity !== null && (
                    <div className="mt-2 text-sm">
                      <span
                        className={`${
                          webinar.isFull ? "text-red-500" : "text-green-600"
                        }`}
                      >
                        {webinar.isFull
                          ? t("webinar.full")
                          : `${webinar.capacity - (webinar.registrationCount || 0)} ${t(
                              "webinar.spotsLeft"
                            )}`}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Registration Form
            <div>
              {/* Back button */}
              {webinars.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedWebinar(null);
                    resetForm();
                  }}
                  className="flex items-center gap-1 text-primary hover:underline mb-4"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  {t("webinar.backToList")}
                </button>
              )}

              {/* Webinar Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-lg">{selectedWebinar.name}</h3>
                <p className="text-gray-600 text-sm mt-1">
                  {selectedWebinar.description}
                </p>
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {formatDate(selectedWebinar.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {selectedWebinar.time}
                  </span>
                </div>
              </div>

              {/* Status Messages */}
              {submitStatus.type && (
                <div
                  className={`mb-4 p-4 rounded-lg ${
                    submitStatus.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              {/* Registration Form */}
              {submitStatus.type !== "success" && (
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("webinar.fullName")} *
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={fullNameChangeHandler}
                        onBlur={fullNameBlurHandler}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors ${
                          fullNameHasError
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder={t("webinar.fullNamePlaceholder")}
                      />
                      {fullNameHasError && (
                        <p className="text-red-500 text-sm mt-1">
                          {t("webinar.fullNameError")}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("webinar.email")} *
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={emailChangeHandler}
                        onBlur={emailBlurHandler}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors ${
                          emailHasError ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder={t("webinar.emailPlaceholder")}
                        dir="ltr"
                      />
                      {emailHasError && (
                        <p className="text-red-500 text-sm mt-1">
                          {t("webinar.emailError")}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("webinar.phone")}
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={phoneChangeHandler}
                        onBlur={phoneBlurHandler}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                        placeholder={t("webinar.phonePlaceholder")}
                        dir="ltr"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || selectedWebinar.isFull}
                    className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                      selectedWebinar.isFull
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        {t("webinar.submitting")}
                      </span>
                    ) : selectedWebinar.isFull ? (
                      t("webinar.full")
                    ) : (
                      t("webinar.registerButton")
                    )}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebinarModal;
